import React from 'react';
import { Card, Row, Col, Typography, Button, Space } from 'antd';
import { 
  SafetyOutlined,
  LoginOutlined, 
  DesktopOutlined, 
  TeamOutlined,
  BarChartOutlined,
  SecurityScanOutlined,
  MonitorOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const { Title, Paragraph, Text } = Typography;

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      icon: <SecurityScanOutlined />,
      title: t('home.features.whitelist_control.title'),
      description: t('home.features.whitelist_control.description')
    },
    {
      icon: <MonitorOutlined />,
      title: t('home.features.real_time_monitoring.title'),
      description: t('home.features.real_time_monitoring.description')
    },
    {
      icon: <TeamOutlined />,
      title: t('home.features.multi_level_policy.title'),
      description: t('home.features.multi_level_policy.description')
    },
    {
      icon: <BarChartOutlined />,
      title: t('home.features.audit_trace.title'),
      description: t('home.features.audit_trace.description')
    },
    {
      icon: <SafetyOutlined />,
      title: t('home.features.zero_trust.title'),
      description: t('home.features.zero_trust.description')
    },
    {
      icon: <DesktopOutlined />,
      title: t('home.features.cross_platform.title'),
      description: t('home.features.cross_platform.description')
    }
  ];

  return (
    <div className="home-container">
      {/* Header */}
      <div className="home-header">
        <div className="header-content">
          <div className="logo-section">
            <BrandLogo size="md" tone="light" />
          </div>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Language switcher in top-right for public page */}
            <LanguageSwitcher />
            <Button
              size="large"
              onClick={() => navigate('/contact')}
            >
              {t('home.cta.contact_us')}
            </Button>
            <Button 
              type="primary" 
              icon={<LoginOutlined />}
              size="large"
              onClick={() => navigate('/login')}
            >
              {t('home.cta.login')}
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-center">
            <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
              <div className="hero-text">
                <Title level={1} className="hero-title">
                  {t('home.hero.subtitle')}
                </Title>
                <Paragraph className="hero-description">
                  {t('home.hero.description_long')}
                </Paragraph>
              </div>
              
              <Space size="large">
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<LoginOutlined />}
                  onClick={() => navigate('/login')}
                >
                  {t('home.cta.try_now')}
                </Button>
                <Button 
                  size="large" 
                  icon={<DesktopOutlined />}
                  onClick={() => {
                    document.getElementById('features').scrollIntoView({ 
                      behavior: 'smooth' 
                    });
                  }}
                >
                  {t('home.cta.learn_features')}
                </Button>
              </Space>
            </Space>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="features-section">
        <div className="section-content">
          <div className="section-header">
            <Title level={2}>{t('home.features.section_title')}</Title>
            <Paragraph>
              {t('home.features.section_description')}
            </Paragraph>
          </div>
          
          <Row gutter={[32, 32]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card className="feature-card" hoverable>
                  <div className="feature-content">
                    <div className="feature-icon">
                      {feature.icon}
                    </div>
                    <Title level={4} className="feature-title">
                      {feature.title}
                    </Title>
                    <Paragraph className="feature-description">
                      {feature.description}
                    </Paragraph>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <Card className="cta-card">
          <div className="cta-content">
            <Title level={2}>{t('home.start.section_title')}</Title>
            <Paragraph>
              {t('home.start.section_description')}
            </Paragraph>
            <Space size="large">
              <Button 
                type="primary" 
                size="large" 
                icon={<LoginOutlined />}
                onClick={() => navigate('/login')}
              >
                {t('home.cta.login')}
              </Button>
              <Button 
                size="large" 
                onClick={() => navigate('/contact')}
              >
                {t('home.cta.contact_us')}
              </Button>
            </Space>
          </div>
        </Card>
      </div>

      {/* Footer */}
      <div className="home-footer">
        <div className="footer-content">
          <Text type="secondary">
            {t('home.footer.copyright')}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Home;
